import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useFormContext } from "react-hook-form";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

const Container = styled.div({
  position: "relative",
  zIndex: 1,
});

const Button = styled.button<{ selectedValue: string }>(
  ({ theme, selectedValue }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.4rem",
    width: "100%",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.regular,
    lineHeight: "2rem",
    padding: "1.6rem",
    border: `1px solid ${theme.color.light_gray}`,
    color:
      selectedValue === "선택" || selectedValue === "Select"
        ? theme.color.light_gray
        : theme.color.black,
  }),
);

const SelectBox = styled(motion.div)<{
  top: number;
  left: number;
  width: number;
}>(({ theme, top, left, width }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  position: "absolute",
  top: `${top}px`,
  left: `${left}px`,
  width: `${width}px`,
  backgroundColor: theme.color.white,
  border: `1px solid ${theme.color.light_gray}`,
  overflowY: "auto",
  maxHeight: "256px",
  zIndex: 10000,
}));

const OptionItem = styled.button<{ last: boolean }>(({ theme, last }) => ({
  width: "100%",
  padding: "10px 30px 10px 20px",
  fontSize: "1.6rem",
  lineHeight: "2.4rem",
  fontWeight: theme.fontWeight.regular,
  color: theme.color.black,
  backgroundColor: theme.color.white,
  border: "none",
  textAlign: "start",
  borderBottom: last ? "none" : `1px solid ${theme.color.border_gray}`,
  ":hover": {
    backgroundColor: theme.color.border_gray,
  },
}));

type Props = {
  options: any[];
  name: string;
  open: boolean;
  setOpen: (value: boolean) => void;
};
const DropdownInput = ({ options = [], name, open, setOpen }: Props) => {
  const [focusName, setFocusName] = useState("");
  // const [selectedValue, setSelectedValue] = useState("선택");
  const { t } = useTranslation();
  const lang = window.localStorage.getItem("language");

  const { setValue, watch } = useFormContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const currentValue = watch(name);

  // 초기 선택값 설정 - 현재 코드값에 해당하는 name_ko 찾기
  // const [selectedValue, setSelectedValue] = useState(
  //   options.find((opt) => opt.code === currentValue)?.name_ko || "선택",
  // );

  const [selectedValue, setSelectedValue] = useState(t("선택"));

  // options나 currentValue가 변경될 때마다 selectedValue 업데이트
  useEffect(() => {
    if (options?.length && currentValue) {
      const matchedOption = options.find((opt) => opt.code === currentValue);
      if (matchedOption) {
        setSelectedValue(
          lang === "en" && matchedOption.name_en
            ? matchedOption.name_en
            : matchedOption.name_ko,
        );
      }
    }
  }, [options, currentValue]);

  const theme = useTheme();

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFocusName(name);
    if (options.length === 0) return;

    setOpen(!open);
  };

  const handleChange = (value: string, code: string) => {
    setOpen(false);
    setSelectedValue(value);
    setValue(name, code);
  };

  useEffect(() => {
    if (focusName !== name) {
      setOpen(false);
    }
  }, [focusName]);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open]);

  return (
    <Container>
      <Button
        onClick={handleToggle}
        type="button"
        selectedValue={selectedValue}
        ref={buttonRef} // 버튼 위치를 참조
      >
        {selectedValue}
        <Image
          src={
            open
              ? theme.image.arrow_up_gray.default
              : theme.image.arrow_down_gray.default
          }
        />
      </Button>

      {open &&
        ReactDOM.createPortal(
          <SelectBox
            top={dropdownPos.top}
            left={dropdownPos.left}
            width={dropdownPos.width}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.1 }}>
            {options.map((item, index) => (
              <OptionItem
                type="button"
                key={index}
                last={index === options.length - 1}
                onClick={() =>
                  handleChange(
                    lang === "en" && item.name_en ? item.name_en : item.name_ko,
                    item.code,
                  )
                }>
                {lang === "en" && item.name_en ? item.name_en : item.name_ko}
              </OptionItem>
            ))}
          </SelectBox>,
          document.body,
        )}
    </Container>
  );
};

export default DropdownInput;
